import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HelpCircle,
  Search,
  Send,
  BookOpen,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

const CATEGORY_LABELS = {
  onboarding: 'Getting Started',
  compliance: 'Compliance',
  technical: 'Technical',
  billing: 'Billing',
};

const Support = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [contactSubject, setContactSubject] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactPriority, setContactPriority] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    fetchKnowledgeBase();
  }, [category, search]);

  const fetchKnowledgeBase = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      const url = `/support/knowledge-base${params.toString() ? `?${params.toString()}` : ''}`;
      const res = await axios.get(url);
      setArticles(res.data.articles || []);
    } catch (err) {
      console.error('Failed to load knowledge base:', err);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!user?.user_id) {
      setSubmitResult({ success: false, message: 'You must be logged in to submit a support request.' });
      return;
    }
    if (!contactMessage.trim()) {
      setSubmitResult({ success: false, message: 'Please enter your message.' });
      return;
    }
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const res = await axios.post('/support/contact', {
        user_id: user.user_id,
        subject: contactSubject.trim() || 'Support Request',
        message: contactMessage.trim(),
        priority: contactPriority,
      });
      setSubmitResult({
        success: true,
        message: res.data.message || 'Request submitted. We typically respond within 24 hours (business days).',
        ticket_id: res.data.ticket_id,
      });
      setContactSubject('');
      setContactMessage('');
      setContactPriority('normal');
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to submit. Please try again.';
      setSubmitResult({ success: false, message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const categories = [...new Set(articles.map((a) => a.category))].filter(Boolean);

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3"
      >
        <HelpCircle className="w-10 h-10 text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Support</h1>
          <p className="text-gray-400">Find answers or contact us</p>
        </div>
      </motion.div>

      {/* Knowledge base / FAQ */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-gray-800 rounded-xl border border-gray-700 p-6"
      >
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <BookOpen className="w-5 h-5 text-blue-400" />
          Knowledge Base & FAQs
        </h2>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All categories</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat] || cat}
              </option>
            ))}
          </select>
        </div>
        {loading ? (
          <div className="text-gray-400 py-8 text-center">Loading...</div>
        ) : articles.length === 0 ? (
          <div className="text-gray-400 py-8 text-center">No articles match. Try a different search or category.</div>
        ) : (
          <ul className="space-y-2">
            {articles.map((art, idx) => {
              const id = art.article_id || art.id || art.title?.replace(/\s+/g, '-').toLowerCase() || idx;
              const isExpanded = expandedId === id;
              return (
                <li
                  key={id}
                  className="rounded-lg bg-gray-700/50 border border-gray-600 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left text-white hover:bg-gray-700 transition-colors"
                  >
                    <span className="font-medium">{art.title}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-0 text-gray-300 text-sm border-t border-gray-600">
                      <p className="mt-3">{art.content}</p>
                      {art.url && (
                        <a
                          href={art.url}
                          className="inline-block mt-2 text-blue-400 hover:underline"
                        >
                          View details →
                        </a>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </motion.section>

      {/* Contact form */}
      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gray-800 rounded-xl border border-gray-700 p-6"
      >
        <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-blue-400" />
          Contact Support
        </h2>
        {!user?.user_id ? (
          <p className="text-gray-400">
            Please log in to submit a support request. You can also email{' '}
            <a href="mailto:support@darkmattersolutions.ai" className="text-blue-400 hover:underline">
              support@darkmattersolutions.ai
            </a>
            .
          </p>
        ) : (
          <form onSubmit={handleContactSubmit} className="space-y-4 max-w-xl">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Subject</label>
              <input
                type="text"
                value={contactSubject}
                onChange={(e) => setContactSubject(e.target.value)}
                placeholder="Brief subject (optional)"
                className="w-full px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Message *</label>
              <textarea
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Describe your question or issue..."
                rows={4}
                required
                className="w-full px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
              <select
                value={contactPriority}
                onChange={(e) => setContactPriority(e.target.value)}
                className="px-4 py-2 rounded-lg bg-gray-700 border border-gray-600 text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            {submitResult && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg ${
                  submitResult.success ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'
                }`}
              >
                {submitResult.success ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{submitResult.message}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Sending...' : 'Send request'}
            </button>
          </form>
        )}
        <p className="mt-4 text-gray-500 text-sm flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Email: support@darkmattersolutions.ai — We typically respond within 24 hours on business days.
        </p>
      </motion.section>
    </div>
  );
};

export default Support;
